import { useGetQueryParams } from '@cp/shared/queryParams';
import { AdminWebPageQueryParams } from 'src/app/shared/const/meta';
import { AdminRoutePath } from 'src/entities/adminRoute';
import { useEffect, useMemo } from 'react';
import { AdminFeedbackViewModel } from '@common/model/admin/catalog/feedback/view';
import { adminCatalogFeedbackApi } from 'src/entities/catalog/feedback';
import { adminProcessingApi } from 'src/entities/processing';
import { DealProcessingEvents, ProcessingViewModel } from '@common/model/processing';
import { ProcessingAndReviewsFiltersType } from '../ui/processingAndReviews';
import { TypeOfItems } from '../lib/config';
import { ProcessingAndReviewsMapper } from './mapper';
import { useProcessingSocket } from 'src/entities/processing';

export type GetItems =
  | {
      typeOfItems: TypeOfItems.Processing;
      items: ProcessingViewModel[];
      totalCount: number;
      isLoading: boolean;
      refetch: () => void;
    }
  | {
      typeOfItems: TypeOfItems.Review;
      items: AdminFeedbackViewModel[];
      totalCount: number;
      isLoading: boolean;
      refetch: () => void;
    };

export const useGetItems = (filterParams?: ProcessingAndReviewsFiltersType | null, withOnlyCount?: boolean): GetItems => {
  const socket = useProcessingSocket();
  const queryParams = useGetQueryParams<AdminWebPageQueryParams<AdminRoutePath.ProcessingAndReviews>>();
  const query = filterParams || queryParams;
  const typeOfItems = query.typeOfItems as TypeOfItems;
  const {
    data: processingData,
    isLoading: isProcessingLoading,
    isFetching: isProcessingFetching,
    isUninitialized,
    refetch: prcsRefetch,
  } = adminProcessingApi.endpoints.getProcessingList.useQuery(ProcessingAndReviewsMapper.toProcessingQuery(query), {
    skip: typeOfItems !== TypeOfItems.Processing,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
  });
  const {
    data: feedbacksData,
    isLoading: isFeedbacksLoading,
    isFetching: isFeedbacksFetching,
    refetch: feedbacksRefetch,
  } = adminCatalogFeedbackApi.endpoints.adminFeedbacks.useQuery(ProcessingAndReviewsMapper.toFeedbacksQuery(query, withOnlyCount), {
    skip: typeOfItems !== TypeOfItems.Review,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
  });

  useEffect(() => {
    const handleUpdate = () => {
      if (!isUninitialized) prcsRefetch();
    };
    socket.on(DealProcessingEvents.Updated, handleUpdate);
    return () => {
      socket.off(DealProcessingEvents.Updated, handleUpdate);
    };
  }, [isUninitialized, prcsRefetch, socket]);

  return useMemo(() => {
    const totalCount = (typeOfItems === TypeOfItems.Processing ? processingData?.totalCount : feedbacksData?.totalCount) || 0;
    const items: GetItems['items'] = (typeOfItems === TypeOfItems.Processing ? processingData?.items : feedbacksData?.items) || [];
    const isLoading = isProcessingLoading || isProcessingFetching || isFeedbacksLoading || isFeedbacksFetching;
    const refetch = typeOfItems === TypeOfItems.Processing ? prcsRefetch : feedbacksRefetch;
    return { items, totalCount, isLoading, typeOfItems, refetch } as GetItems;
  }, [
    processingData?.items,
    processingData?.totalCount,
    prcsRefetch,
    feedbacksData?.items,
    feedbacksData?.totalCount,
    feedbacksRefetch,
    isProcessingFetching,
    isProcessingLoading,
    isFeedbacksFetching,
    isFeedbacksLoading,
    typeOfItems,
  ]);
};
